import { featureModuleTypesEnum } from "helpers/enums/featureModuleTypes";
import { waybillStateFlags, waybillStateFlagsEnum } from "helpers/enums/waybillStateFlags";
import { waybillTitleTypesEnum } from "helpers/enums/waybillTitleTypes";
import { waybillTitleStatusesEnum } from "helpers/enums/waybillTitleStatuses";
import { isTruckingOrganizationLogist, type RolesModel } from "helpers/roles";

interface TitleRejectionState {
    type: string;
    rejectionReason?: string | null;
}

const archivedFlag = waybillStateFlags.find(({ value }) => value === waybillStateFlagsEnum.Archived)?.intValue;

/** Read archive state only from the operator's bit flags. */
export const isWaybillArchived = (intStateFlags: number) =>
    archivedFlag !== undefined && (intStateFlags & archivedFlag) === archivedFlag;

/** A rejection reason takes precedence over signing and title processing state. */
export const isWaybillTitleRejected = (title: { rejectionReason?: string | null }) => !!title.rejectionReason;

/** Use the server's signature validation result before any local signing metadata. */
export const isWaybillTitleSignatureInvalid = (title?: { status?: string } | null) =>
    title?.status === waybillTitleStatusesEnum.InvalidSignature;

/** Allow signature retries under the same bid-status rules as failed sending. */
export const isWaybillTitleSigningRetry = (title: { status: string }) =>
    title.status === waybillTitleStatusesEnum.FailedToSend || isWaybillTitleSignatureInvalid(title);

/** Allow signing only for the latest response title of its type. */
export const canSignWaybillResponseTitle = (
    title: { id: number; type: string; status: string },
    titles: { id: number; type: string }[],
) => {
    const latestTitle = titles.reduce<(typeof titles)[number] | undefined>(
        (latest, item) => (item.type === title.type && (!latest || item.id > latest.id) ? item : latest),
        undefined,
    );
    return (
        title.id === latestTitle?.id &&
        [waybillTitleTypesEnum.T2, waybillTitleTypesEnum.T4, waybillTitleTypesEnum.T8].includes(title.type) &&
        (title.status === waybillTitleStatusesEnum.ReceivedOrCreated || isWaybillTitleSigningRetry(title))
    );
};

/** Permit QR codes only for the latest T2 with a usable signature. */
export const canDownloadWaybillQrCodes = (titles: { id: number; type: string; status: string; signed: boolean }[]) => {
    const title = titles.reduce<(typeof titles)[number] | undefined>(
        (latest, item) => (item.type === waybillTitleTypesEnum.T2 && (!latest || item.id > latest.id) ? item : latest),
        undefined,
    );
    return !!title?.signed && !isWaybillTitleSigningRetry(title);
};

/** Classify a compact title step with rejection and invalid signatures before signing. */
export const getWaybillTitleTimelineState = (
    title?: { signed: boolean; status: string; rejectionReason?: string | null } | null,
) => {
    if (!title) {
        return "empty";
    }
    if (isWaybillTitleRejected(title)) {
        return "rejected";
    }
    if (isWaybillTitleSignatureInvalid(title)) {
        return "error";
    }
    if (title.signed) {
        return "signed";
    }
    return title.status === waybillTitleStatusesEnum.Failed || title.status === waybillTitleStatusesEnum.FailedToSend
        ? "error"
        : "pending";
};

/** Prevent confirmation and all T2 actions when the source T1 was rejected. */
export const isWaybillTitleActionBlocked = (title: TitleRejectionState, titles: TitleRejectionState[]) =>
    isWaybillTitleRejected(title) ||
    ((title.type === waybillTitleTypesEnum.T1 || title.type === waybillTitleTypesEnum.T2) &&
        titles.some((source) => source.type === waybillTitleTypesEnum.T1 && isWaybillTitleRejected(source)));

export const nextSignedWaybillTitleWarning =
    "Подписание титула недоступно в связи с наличием следующего подписанного титула";

/** Block only T1/T3 confirmation when any corresponding T2/T4 is signed. */
export const hasSignedNextWaybillTitle = (title: { type: string }, titles: { type: string; signed: boolean }[]) => {
    const nextType =
        title.type === waybillTitleTypesEnum.T1
            ? waybillTitleTypesEnum.T2
            : title.type === waybillTitleTypesEnum.T3
              ? waybillTitleTypesEnum.T4
              : undefined;
    return nextType !== undefined && titles.some((next) => next.type === nextType && next.signed);
};

/** Match the backend's TruckingOrganizationLogists role policy and module availability. */
export const canArchiveWaybills = (roles: RolesModel, availableFeatureModules: string[]) =>
    availableFeatureModules.includes(featureModuleTypesEnum.Waybills) && isTruckingOrganizationLogist(roles);
